import { v4 } from 'uuid';

import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import { PerfAggregator } from 'src/utils/perf/aggregator';
import { PerfWindowSnapshot, TracerStat } from 'src/utils/perf/types';

import { SegmentCollector } from './segment-collector';
import { FlushReason, PerfEventPayload, TracerPayloadEntry } from './types';
import { round2, roundNullable } from './utils';

const MIN_FLUSH_DURATION_MS = 5_000;
const SCHEMA_VERSION = 1;

const buildTracerPayload = (tracer: Record<string, TracerStat>): TracerPayloadEntry[] => {
  return Object.values(tracer).map((stat) => ({
    label: stat.label,
    kind: stat.kind,
    count: stat.count,
    rate: round2(stat.rate),
    p50: round2(stat.p50),
    p95: round2(stat.p95),
    max: round2(stat.max),
    cost: round2(stat.cost),
  }));
};

/**
 * 한 세션 동안의 윈도우/매치 시퀀스를 들고 있다가
 * 윈도우 종료(또는 페이지 이탈) 시 이벤트 게이트웨이로 페이로드를 전송한다.
 *
 * 라이프사이클은 store.ts가 소유 (initPerfStore에서 생성, destroyPerfStore에서 폐기).
 * index.ts의 startMatchWindow는 싱글턴 getter를 통해 onMatchStart()를 호출한다.
 */
export class PerfEventReporter {
  private readonly sessionId: string;

  private readonly sessionStartedAt: number;

  private windowSequence = 0;

  private matchSequence = 0;

  private segmentCollector: SegmentCollector | null = null;

  constructor() {
    this.sessionId = v4();
    this.sessionStartedAt = performance.now();
  }

  public onMatchStart(): void {
    this.matchSequence += 1;
  }

  public onWindowTransition(): void {
    this.windowSequence += 1;
    this.segmentCollector = null;
  }

  public tickSegment(aggregator: PerfAggregator): void {
    const current = aggregator.getCurrent();
    if (!current || current.kind === 'mirror') return;

    if (!this.segmentCollector) {
      this.segmentCollector = new SegmentCollector(current.startedAtEpoch);
    }
    this.segmentCollector.tick(current);
  }

  public report(snapshot: PerfWindowSnapshot, flushReason: FlushReason): void {
    if (flushReason === 'page_leave' && snapshot.durationMs < MIN_FLUSH_DURATION_MS) return;

    pushEvent({
      eventType: EVENT_TYPE.DEBUG,
      eventName: EVENT_NAME.DEBUG__WEB_PERFORMANCE_SUMMARY,
      eventParams: this.buildPayload(snapshot, flushReason),
    });
  }

  private buildPayload(snapshot: PerfWindowSnapshot, flushReason: FlushReason): PerfEventPayload {
    return {
      schema_version: SCHEMA_VERSION,
      match_id: snapshot.matchId,
      session_id: this.sessionId,
      window_sequence: this.windowSequence,
      window_started_at: snapshot.startedAtEpoch,
      match_sequence: snapshot.kind === 'match' ? this.matchSequence : 0,
      session_elapsed_ms: Math.round(performance.now() - this.sessionStartedAt),
      window_kind: snapshot.kind,
      duration_ms: snapshot.durationMs,
      flush_reason: flushReason,
      page_fps_p50: roundNullable(snapshot.fps?.p50),
      page_fps_p95: roundNullable(snapshot.fps?.p95),
      page_fps_p05: roundNullable(snapshot.fps?.p05),
      local_pipe_fps_p50: roundNullable(snapshot.videoFps.local.render?.p50),
      local_pipe_fps_p95: roundNullable(snapshot.videoFps.local.render?.p95),
      local_pipe_fps_p05: roundNullable(snapshot.videoFps.local.render?.p05),
      local_send_pipe_fps_p50: roundNullable(snapshot.videoFps.local.sendRender?.p50),
      local_send_pipe_fps_p95: roundNullable(snapshot.videoFps.local.sendRender?.p95),
      local_send_pipe_fps_p05: roundNullable(snapshot.videoFps.local.sendRender?.p05),
      local_source_fps_p50: roundNullable(snapshot.videoFps.local.source?.p50),
      local_source_fps_p95: roundNullable(snapshot.videoFps.local.source?.p95),
      local_encode_fps_p50: roundNullable(snapshot.videoFps.local.encode?.p50),
      local_encode_fps_p95: roundNullable(snapshot.videoFps.local.encode?.p95),
      peer_in_fps_p50: roundNullable(snapshot.videoFps.peer.source?.p50),
      peer_in_fps_p95: roundNullable(snapshot.videoFps.peer.source?.p95),
      peer_pipe_fps_p50: roundNullable(snapshot.videoFps.peer.render?.p50),
      peer_pipe_fps_p95: roundNullable(snapshot.videoFps.peer.render?.p95),
      peer_pipe_fps_p05: roundNullable(snapshot.videoFps.peer.render?.p05),
      tracer: buildTracerPayload(snapshot.tracer),
      segments: this.segmentCollector?.getSegments() ?? [],
    };
  }
}

let instance: PerfEventReporter | null = null;

/**
 * 모듈 내 싱글턴 인스턴스를 생성한다. store.ts(라이프사이클 소유자)에서 호출.
 * HMR 등으로 두 번 호출되면 이전 인스턴스의 카운터/버퍼는 폐기된다.
 */
export const initPerfEventReporter = (): PerfEventReporter => {
  instance = new PerfEventReporter();
  return instance;
};

export const destroyPerfEventReporter = (): void => {
  instance = null;
};

/**
 * 싱글턴 접근자. 초기화 전이면 null. 호출자는 graceful no-op 처리해야 한다.
 */
export const getPerfEventReporter = (): PerfEventReporter | null => instance;
